import { live } from './data/live';
import { schedule } from './data/schedule';
import { inforoll } from './renderers/inforoll';
import { donationsRenderer } from './renderers/donations-renderer';
import { flagRenderer } from './renderers/flag-renderer';
import { incentiveFlag } from './renderers/incentive-flag-renderer';

/**
 * The infobar cycle, as a table instead of a chain of callbacks.
 *
 * Each segment plays, resolves, and the driver picks the next one whose `when()` holds.
 * Nothing knows its successor, which is what lets the panels be reordered, skipped or
 * added without editing three other functions — and what removes the
 * `infobar → overlay-renderer → flag-renderer → infobar` import cycle, since neither
 * renderer has to reach for the next one any more.
 *
 * Two things the old chain could not do:
 *
 *  - `?stopLoop` is one check here rather than six `if (window.stopLoop) return` guards
 *    scattered across three files, where forgetting one silently broke debug mode.
 *  - every segment has a deadline. anime.js runs on requestAnimationFrame, which OBS
 *    throttles for inactive browser sources; if a `complete:` handler never fired the
 *    old chain stopped dead until someone reloaded the source. Now the segment is
 *    abandoned and the cycle carries on.
 */

/** Rejects after `ms`, so a stalled segment cannot wedge the cycle. */
function deadline(ms, name) {
    return new Promise((_, reject) => {
        setTimeout(() => reject(new Error(`segment "${name}" did not finish within ${ms} ms`)), ms);
    });
}

export const cycle = {
    /** 'idle' | 'frozen' | the name of the segment on screen. Readable from the console. */
    state: 'idle',
    running: false,
    index: 0,

    segments: [
        {
            name: 'inforoll',
            when: () => true,
            maxMs: 150_000,
            enter: () => inforoll.play(),
            // A frozen inforoll parks the cycle: the bar holds one static line and no
            // panel follows it. That has always been the behaviour, but as a side effect
            // of advanceNextRow() returning early — here it is a decision.
            haltAfter: () => live.getFreezeInforoll(),
        },
        {
            name: 'goal',
            when: () => true,
            maxMs: 45_000,
            enter: () => donationsRenderer.playDonationGoal(),
        },
        {
            name: 'red',
            when: () => typeof schedule.nextRun !== 'undefined',
            maxMs: 30_000,
            enter: () => flagRenderer.playRed(),
        },
        {
            name: 'white',
            when: () => schedule.next5.length > 1,
            maxMs: 50_000,
            enter: () => flagRenderer.playWhite(),
        },
        {
            name: 'green',
            when: () => live.incentives.length > 0,
            maxMs: () => 20_000 * live.incentives.length + 20_000,
            enter: () => incentiveFlag.playGreen(),
        },
        {
            // Always runs, including after a skipped panel: it retracts whatever is out
            // and re-derives the schedule before the next pass reads it.
            name: 'retract',
            when: () => true,
            maxMs: 25_000,
            enter: () => flagRenderer.playRetract(),
        },
    ],

    start: () => {
        if (cycle.running) return;
        cycle.running = true;
        cycle.index = 0;
        cycle.loop();
    },

    stop: () => {
        cycle.running = false;
        cycle.state = 'idle';
    },

    loop: async () => {
        while (cycle.running) {
            const segment = cycle.segments[cycle.index];
            cycle.index = (cycle.index + 1) % cycle.segments.length;

            if (!segment.when()) continue;

            cycle.state = segment.name;
            const limit = typeof segment.maxMs === 'function' ? segment.maxMs() : segment.maxMs;

            try {
                await Promise.race([segment.enter(), deadline(limit, segment.name)]);
            } catch (error) {
                // A stalled or throwing segment loses its turn; the cycle does not.
                console.error(`Cycle segment "${segment.name}" failed:`, error);
            }

            if (segment.haltAfter?.()) {
                cycle.state = 'frozen';
                cycle.running = false;
                return;
            }

            // ?stopLoop: play the segment that was asked for, then park. One check, where
            // the old chain needed a guard inside every hand-off.
            if (window.stopLoop) {
                cycle.running = false;
                cycle.state = 'idle';
                return;
            }
        }
    },
};

window.cycle = cycle;
